import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import Box from '@mui/material/Box'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Modal from '@/v2/components/common/Modal'
import TextField from '@/v2/components/common/TextField'
import SelectField from '@/v2/components/common/SelectField'
import CheckboxField from '@/v2/components/common/CheckboxField'
import Typography from '@/v2/components/common/Typography'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { useSettingsBookingPages } from '@/v2/hooks/settings/useSettingsBookingPages'
import { useUpdateCancellationNotice } from '@/v2/hooks/settings/useSettingsCancellationNotice'
import {
  Note,
  TextArea,
  CodeArea,
  Tokens,
  Token,
  MessagePreview,
  Column,
  tokenInserter,
} from '../reschedule/editorParts'
import {
  CANCELLATION_TOKENS,
  REASONS,
  bookingPageUrl,
  compile,
  fill,
  parse,
  sampleValues,
} from './cancellationTemplate'

const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px;
  align-items: start;
`

const PreviewLabel = styled.span`
  font-size: 0.75rem;
  color: #6b7280;
`

const EmailPreview = styled.div`
  margin-top: 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  overflow: hidden;
  font-size: 0.85rem;

  div:first-child {
    padding: 0.5rem 0.75rem;
    background: #f9fafb;
  }

  div:last-child {
    padding: 0.75rem;
    line-height: 1.5;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
`

const MESSAGE_FIELDS = ['smsMessage', 'emailSubject', 'emailMessage']

// Each message in plain text when it can be read that way, the raw Liquid
// otherwise. `code` remembers which, so saving writes each one back the way
// it came.
const toDraft = notice => {
  const draft = { ...notice, reasons: { ...notice.reasons }, code: {} }
  MESSAGE_FIELDS.forEach(field => {
    const plain = parse(notice[field])
    draft.code[field] = plain === null
    draft[field] = plain === null ? notice[field] || '' : plain
  })
  return draft
}

const fromDraft = ({ code, ...draft }) => {
  const notice = { ...draft }
  MESSAGE_FIELDS.forEach(field => {
    notice[field] = code[field] ? draft[field] : compile(draft[field])
  })
  return notice
}

const MessageBox = ({ label, rows, value, code, onChange }) => {
  const box = useRef(null)
  const insert = tokenInserter(box, value, onChange)
  const Area = code ? CodeArea : TextArea

  return (
    <Column>
      <Area
        ref={box}
        aria-label={label}
        rows={rows}
        value={value}
        spellCheck={!code}
        onChange={event => onChange(event.target.value)}
      />
      {!code && (
        <Tokens>
          Add:
          {CANCELLATION_TOKENS.map(({ label: token }) => (
            <Token key={token} type="button" onClick={() => insert(token)}>
              {token}
            </Token>
          ))}
        </Tokens>
      )}
    </Column>
  )
}

const CancellationNoticeModal = ({ isOpen, notice, onClose }) => {
  const { onOpenSnackbar } = useSnackbarContext()
  const { bookingPages } = useSettingsBookingPages()
  const { updateNotice, isLoading: isSaving } = useUpdateCancellationNotice()
  const [draft, setDraft] = useState(null)
  const [tab, setTab] = useState('sms')

  useEffect(() => {
    if (isOpen && notice) {
      setDraft(toDraft(notice))
      setTab('sms')
    }
  }, [isOpen, notice])

  const set = (field, value) => setDraft(current => ({ ...current, [field]: value }))
  const setReason = (key, value) =>
    setDraft(current => ({
      ...current,
      reasons: { ...current.reasons, [key]: value },
    }))

  const handleSave = async () => {
    try {
      await updateNotice({
        ...fromDraft(draft),
        bookingPageId: draft.bookingPageId ?? bookingPages[0]?.id ?? null,
      })
      onOpenSnackbar('Cancellation notice saved', 'success')
      onClose()
    } catch (error) {
      onOpenSnackbar(error.message || 'Failed to save the notice', 'error')
    }
  }

  const selectedPage = bookingPages.find(
    page => page.id === (draft?.bookingPageId ?? bookingPages[0]?.id)
  )
  const samples =
    draft &&
    sampleValues(draft.reasons.both, {
      bookingUrl: bookingPageUrl(notice?.bookingPageUrl, selectedPage?.slug),
    })
  const preview = field =>
    draft.code[field] ? '' : fill(draft[field], samples)

  return (
    <Modal
      isOpen={isOpen}
      title="Edit Cancellation Notice"
      onClose={onClose}
      onConfirm={handleSave}
      confirmLabel="Save Changes"
      cancelLabel="Cancel"
      submitDisabled={isSaving || !draft}
      loading={!draft}
      maxWidth="md"
      fullWidth
    >
      {draft && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography bold fontSize="1rem">
              Delivery Methods
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
              <CheckboxField
                label="Send by Email"
                checked={draft.byEmail}
                onChange={event => set('byEmail', event.target.checked)}
              />
              <CheckboxField
                label="Send by SMS"
                checked={draft.bySms}
                onChange={event => set('bySms', event.target.checked)}
              />
            </Box>
          </Box>

          <Box>
            <Typography bold fontSize="1rem">
              Booking link
            </Typography>
            <Note style={{ margin: '0.25rem 0 0.75rem' }}>
              The booking page the notice links to, so the patient can pick a
              new time.
            </Note>
            <SelectField
              fullWidth
              noMargin
              label="Booking page"
              options={bookingPages.map(page => ({
                value: page.id,
                label: page.title,
              }))}
              value={draft.bookingPageId ?? bookingPages[0]?.id ?? ''}
              onChange={value => set('bookingPageId', Number(value))}
            />
          </Box>

          <Box>
            <Typography bold fontSize="1rem">
              Reasons
            </Typography>
            <Note style={{ margin: '0.25rem 0 0.75rem' }}>
              Staff pick the reason when they cancel. This is the wording that
              replaces [Reason] in the message.
            </Note>
            <Box sx={{ display: 'grid', gap: 2 }}>
              {REASONS.map(reason => (
                <TextField
                  key={reason.key}
                  fullWidth
                  label={reason.label}
                  value={draft.reasons[reason.key]}
                  onChange={value => setReason(reason.key, value)}
                />
              ))}
            </Box>
          </Box>

          <Box>
            <Tabs
              value={tab}
              onChange={(event, value) => setTab(value)}
              sx={{ mb: 2 }}
            >
              <Tab value="sms" label="SMS message" />
              <Tab value="email" label="Email message" />
            </Tabs>

            {tab === 'sms' ? (
              <Columns>
                <Column>
                  <MessageBox
                    label="SMS message"
                    rows={6}
                    value={draft.smsMessage}
                    code={draft.code.smsMessage}
                    onChange={value => set('smsMessage', value)}
                  />
                  <Note style={{ marginTop: '0.75rem' }}>
                    [Health history link] is left out when the patient already
                    completed it.
                  </Note>
                </Column>
                <Column>
                  <PreviewLabel>Preview</PreviewLabel>
                  <MessagePreview message={preview('smsMessage')} />
                </Column>
              </Columns>
            ) : (
              <Columns>
                <Column>
                  <MessageBox
                    label="Email subject"
                    rows={1}
                    value={draft.emailSubject}
                    code={draft.code.emailSubject}
                    onChange={value => set('emailSubject', value)}
                  />
                  <Box sx={{ height: 12 }} />
                  <MessageBox
                    label="Email message"
                    rows={10}
                    value={draft.emailMessage}
                    code={draft.code.emailMessage}
                    onChange={value => set('emailMessage', value)}
                  />
                </Column>
                <Column>
                  <PreviewLabel>Preview</PreviewLabel>
                  <EmailPreview>
                    <div>
                      Subject: <b>{preview('emailSubject')}</b>
                    </div>
                    <div>{preview('emailMessage')}</div>
                  </EmailPreview>
                </Column>
              </Columns>
            )}
          </Box>
        </Box>
      )}
    </Modal>
  )
}

export default CancellationNoticeModal
